import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';

type Genre = {
  id: string;
  slug: string;
  name: string;
  kind: 'system' | 'user';
  status: 'active' | 'archived' | 'proposed';
  createdBy: string | null;
  votingEndsAt: string | null;
  voteCount: number;
  iVoted: boolean;
  packCount: number;
};

export const Route = createFileRoute('/genres/')({
  head: () =>
    buildHead({
      title: 'Genres',
      description:
        'Browse system and community-proposed genres. Vote on new genres to be added to the battle catalogue. Trap, house, drum and bass, lo-fi, phonk, and more.',
      path: '/genres',
    }),
  component: GenresList,
});

function GenresList() {
  const { data: session } = useSession();
  const [items, setItems] = useState<Genre[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const res = await api<{ items: Genre[] }>('/genres?includeProposed=true');
    setItems(res.items);
    setLoading(false);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: once on mount
  useEffect(() => {
    load();
  }, []);

  async function vote(g: Genre, undo: boolean) {
    await api(`/genres/${g.id}/vote`, { method: undo ? 'DELETE' : 'POST' });
    load();
  }

  const active = items.filter((i) => i.status === 'active');
  const proposed = items.filter((i) => i.status === 'proposed');

  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-10">
      <header className="flex items-baseline justify-between">
        <h2 className="text-2xl font-bold">Genres</h2>
        {session && (
          <Link
            to="/genres/propose"
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
          >
            Propose new
          </Link>
        )}
      </header>

      {loading && <p>Loading...</p>}

      {!loading && proposed.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">Up for vote</h3>
          <ul className="space-y-2">
            {proposed.map((g) => (
              <li
                key={g.id}
                className="flex items-center justify-between rounded-md border border-border p-3"
              >
                <div>
                  <div className="font-medium">{g.name}</div>
                  <div className="text-xs text-muted-foreground">
                    <code>{g.slug}</code> ·{' '}
                    {g.votingEndsAt ? `${daysLeft(g.votingEndsAt)} days left` : 'window closed'}
                    {' · '}
                    {g.packCount} {g.packCount === 1 ? 'pack' : 'packs'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">{g.voteCount}</span>
                  {session ? (
                    <button
                      type="button"
                      onClick={() => vote(g, g.iVoted)}
                      className={`rounded-md px-3 py-1 text-sm ${
                        g.iVoted ? 'bg-muted text-foreground' : 'bg-primary text-primary-foreground'
                      }`}
                    >
                      {g.iVoted ? 'Voted' : 'Vote'}
                    </button>
                  ) : (
                    <a href="/auth/sign-in" className="text-sm underline">
                      Sign in to vote
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Active genres</h3>
        {active.length === 0 && <p className="text-sm text-muted-foreground">None yet.</p>}
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {active.map((g) => (
            <li key={g.id} className="rounded-md border border-border p-3">
              <div className="font-medium">{g.name}</div>
              <div className="text-xs text-muted-foreground">
                {g.kind} · {g.packCount} {g.packCount === 1 ? 'pack' : 'packs'}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function daysLeft(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
}
